import { sharedOnboarding } from "@shared/locales/en/US/sharedOnboarding";
import { ModularStoreSetupSteps } from "@shared/types/ModularStoreSetupSteps";

const onboarding = {
  ...sharedOnboarding,
  AccountSetup: {
    AccountSetup: "Account setup",
    percentComplete: "{{percentage}}% complete",
    AccountSetupProgress: "Account setup progress",
    AccountSetupComplete: "Account setup complete",
    Congratulations: "Congratulations!",
    StepsLeft: "{{steps_left}} of {{total_steps}} steps completed",
    AccountSetupTitle: "Finish setting up your account",
    Done: "Done",
    RemindMeLater: "Remind me later",
    CloseAccountSetupMenu: "Close account setup menu",
    CredentialStatus: {
      SubmitCredentials:
        "Submit your NPI or healthcare credentials to access all of Fullscript's features.",
      PendingReview:
        "Your credentials are under review. The review typically takes less than one business day.",
      DidNotMeetRequirements:
        "Your credentials didn't meet the requirements for a Fullscript account. You can submit again, or <0>learn more</0> about how to submit your credentials.",
      Approved: "Congrats, your credentials have been approved!",
      NextStep: "As a next step, complete one of the items listed below.",
      UploadMoreCredentials: "Upload more credentials",
      ResubmitCredentials: "Resubmit credentials",
    },
    MenuItems: {
      Buttons: {
        NotRightNow: "Not right now",
        SetupProfitAccount: "Set up profit",
        RegisterForLabs: "Register for labs",
        SetUpEHR: "Set up EHR",
        AddStaff: "Add staff",
        AddCredentials: "Add credentials",
      },
      Modals: {
        SkipSetupProfitAccount: {
          title: "Skip setup profit account?",
          body: "You'll stay as a no-profit account. Your patients will receive a 10% discount on supplements and personal care items, with an additional 5% for auto refills. Excludes labs products. <learnMore>Learn more about profit</learnMore>",
          canadaBody:
            "You'll stay as a no-profit account. Your patients will receive a 10% discount on supplements and personal care items, with an additional 5% for auto refills. <learnMore>Learn more about profit</learnMore>",
          button: "Not interested in profit",
        },
        SkipAddPractitionersAndStaff: {
          title: "Skip adding practitioners and staff?",
          body: "If you need to add any practitioners or staff later, you can go to <staffSettings>Staff settings.</staffSettings>",
          button: "I don't have staff",
        },
        SkipRegisterForLabs: {
          title: "Skip registering for labs?",
          body: "If you decide to use labs in the future, go to the <labRecommendations>Labs catalog</labRecommendations> or <accountSettings>Dispensary settings</accountSettings> to complete your registration.",
          button: "Not interested in labs",
        },
        SkipConnectYourEHR: {
          title: "Skip connecting your EHR?",
          body: "You can integrate an EHR later in <accountSettings>Dispensary settings</accountSettings>",
          body2:
            "If we don't have the EHR you're looking for, <requestIntegration>request a specific integration.</requestIntegration>",
          button: "Don't want to connect",
        },
      },
      Titles: {
        SetupProfitAccount: "Set up a profit account",
        RegisterForLabs: "Register for labs",
        ConnectYourEHR: "Connect your EHR",
        AddPractitionersAndStaff: "Add practitioners and staff",
        AddHealthcareCredentials: "Add healthcare credentials",
      },
      Content: {
        SetupProfitAccountTooltip:
          "Submit your NPI or healthcare credentials to setup a profit account with Fullscript.",
        SetupProfitAccount:
          "Offer flexible, personalized discounts that make care more affordable and keep {{patientTermPlural}} engaged long term, while earning up to {{percentage}} on supplements and personal care items.",
        AddPractitionersAndStaff:
          "Your Fullscript dispensary supports multiple practitioners and staff.",
        ConnectYourEHR:
          "Integrate Fullscript with your EHR to seamlessly dispense supplements without leaving your platform.<br /><br />See our <IntegrationsSupportPage>Integrations Support page</IntegrationsSupportPage> for help on how to set up different EHRs.",
        RegisterForLabs:
          "Registration is required to recommend blood labs and specialty kits. This usually takes less than one minute. <learnMore>Learn more</learnMore>",
        RegisterForLabsTooltip:
          "Submit your NPI or healthcare credentials to register for labs with Fullscript.",
      },
    },
  },
  AttnSignMaps: "Attention: Sign MAPs",
  AttnTaxExempt: "Attention: Tax exemption",
  AttnTaxExemptionBlurb:
    "Fullscript will not charge or collect tax for any wholesale orders by a Reseller who produces a valid resale or other applicable certificate which matches their ship-to-state.",
  BeforeYouBuyWholesale: "Before you buy wholesale",
  Both: "Both",
  Labs: "Labs",
  BuyingWholesale: "Buying wholesale",
  WholesaleOrdering: "Wholesale ordering",
  CompleteMaps: "Complete MAPs",
  Dismiss: "Dismiss",
  DispenseVirtually: "Dispense virtually",
  DispenseProducts: "Dispense some products in-office, or recommend supplements online",
  LabsDiagnostics:
    "Affordable pricing, leading lab partners, and fast, free shipping for diagnostic kits",
  SendOnlinePlans: "Send online plans to {{patientTermPlural}}, and stock your shelves in-office.",
  EnterMyDispensary: "Enter my dispensary",
  FullscriptMapsRequirements:
    "Fullscript requires purchasers to agree with and adhere to the MAP policy prior to purchasing their products.",
  HowCanHelp: "How can Fullscript help you?",
  KnowHowYou: "Let us know how you'll use Fullscript",
  HowDoYouWant: "How do you want to use Fullscript?",
  WhatUseFor: "What will you be using Fullscript for?",
  IJustWantToSeeTheSoftware: "I just want to see the software.",
  NextButton: "Next",
  ContinueButton: "Continue",
  OnlineDispensing: "Online dispensing",
  OnlinePlans: "Online plans",
  ProductsAttachmentsRecommendation:
    "Send products, attachments, and {{treatmentPlanTermPlural}} to your {{patientTermPlural}}.",
  PurchaseProductsFrom: "Purchase products from over 100+ brands to sell in-office.",
  RecommendSupplements: "Recommend supplements online that ship directly to your patients' home",
  BuildIndividual:
    "Build individual or multi-{{patientTerm}} plans that {{patientTermPlural}} order to their doors.",
  SelectAllTheWays: "Select all ways that you want to use Fullscript.",
  ShopQualityAssured:
    "Shop 9,000+ quality-assured products from brands you love at wholesale prices",
  ShopWholesale: "Shop the wholesale catalog to stock your shelves in-office.",
  ThisWontImpact: "This won't impact your overall experience.",
  LetUsKnow:
    "This won't affect your overall experience, but it'll help us introduce you to the right tools. If you're unsure, select online plans.",
  SelectAllThatApply: "Select all that apply to you.",
  WeAskThisSo: "We ask this to get you the right tools for you and your {{patientTermPlural}}.",
  Yes: "Yes",
  No: "No",
  SetUpProfitAccount: "Set up profit account",
  GoBackToMargin: "Back to account type",
  GoBackButton: "Back to {{ prevStepName }}",
  nextStepButton: "Next: {{ stepName }}",
  provideDispensaryAddress: "Provide dispensary address",
  modalCTA: "Submit",
  doneTitle: "You're almost done, {{ practitionerName }}",
  CompleteSignUp: "Complete sign up",
  certUpExperiment: {
    stepName: {
      [ModularStoreSetupSteps.CERTIFICATE]: "Upload credentials",
      [ModularStoreSetupSteps.STUDENT_IDENTIFICATION]: "Student identification",
      [ModularStoreSetupSteps.COMPLETE]: "Complete setup",
    },
    previousStepName: {
      [ModularStoreSetupSteps.CERTIFICATE]: "credentials",
      [ModularStoreSetupSteps.STUDENT_IDENTIFICATION]: "student identification",
    },
  },
  stepName: {
    [ModularStoreSetupSteps.CERTIFICATE]: "Upload credentials",
    [ModularStoreSetupSteps.STUDENT_IDENTIFICATION]: "Student identification",
    [ModularStoreSetupSteps.COMPLETE]: "Complete setup",
  },
  previousStepName: {
    [ModularStoreSetupSteps.CERTIFICATE]: "credentials",
    [ModularStoreSetupSteps.STUDENT_IDENTIFICATION]: "student identification",
  },
  practitionerSignupStepper: {
    step1: "Get started",
    step2: "Create account",
    step3: "Practice info",
    step4: "Credentials",
  },
} as const;

export { onboarding };
